import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Tooltip,
  IconButton,
  Stack,
  Fab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DBBackupFile } from "../models/DatabaseBackUp/DBBackupFile";
import agent from "../../api/agent";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import BackupIcon from "@mui/icons-material/Backup";
import DeleteIcon from "@mui/icons-material/Delete";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";

export default function DBBackupDashboard() {
  const [loading, setLoading] = useState<boolean>(true);
  const [backupFiles, setBackupFiles] = useState<DBBackupFile[]>([]);

  //  For Azure backup loading
  const [backupLoading, setBackupLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");

  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  // For Azure backup delete function
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          const logResponse =
            await agent.DatabaseExportService.listBackupFiles(tokenFLocal);
          setBackupFiles(logResponse);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching backup files:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBackup = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setBackupLoading(true);
    try {
      await agent.DatabaseExportService.exportToBacpac(token);
    } catch (error) {
      console.error("Error exporting to Bacpac:", error);
      showToast("Error exporting database backup. Please try again.", "error");
    } finally {
      setBackupLoading(false);
      showToast(
        "Database backup started successfully! Please wait about 5 minutes.",
        "success",
      );
    }
  };

  const handleDownload = async (fileName: string) => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setDownloadLoading(true);
    try {
      const response = await agent.DatabaseExportService.downloadbackup(
        fileName,
        token,
      );
      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName; // or response.headers['content-disposition'] to get filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast(
        "Backup file download started! Please check your downloads folder.",
        "success",
      );
    } catch (error) {
      console.error("Error downloading backup file:", error);
      showToast("Error downloading backup file. Please try again.", "error");
    } finally {
      setDownloadLoading(false);
    }
  };

  // triggered the double confirmation dialog for delete backup file
  function handleDeleteOnClick(fileName: string) {
    setSelectedFile(fileName);
    setDeleteDialogOpen(true);
  }

  function handleDeleteDialogClose() {
    setSelectedFile("");
    setDeleteDialogOpen(false);
  }

  const handleConfirmDelete = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedFile === "") return;
    setLoading(true);
    try {
      await agent.DatabaseExportService.deleteBackup(selectedFile, token);
      const logResponse =
        await agent.DatabaseExportService.listBackupFiles(token);
      setBackupFiles(logResponse);
      showToast("Backup file deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting backup file:", error);
      showToast("Error deleting backup file. Please try again.", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose();
    }
  };

  if (loading) return <LoadingComponent content="Loading Data..." />;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Database Backup Dashboard</Typography>
        <Tooltip title="Export to CSV" followCursor>
          <span>
            <Fab
              color="success"
              onClick={() => handleBackup()}
              size="small"
              disabled={backupLoading}
            >
              {backupLoading ? <CircularProgress size={24} /> : <BackupIcon />}
            </Fab>
          </span>
        </Tooltip>
      </Stack>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>File Size</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell>Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {backupFiles.map((file) => (
              <TableRow key={file.fileName}>
                <TableCell>{file.fileName}</TableCell>
                <TableCell>{file.fileSize}</TableCell>
                <TableCell>
                  {new Date(file.lastModified).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Download Backup File">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(file.fileName)}
                        disabled={downloadLoading}
                      >
                        {downloadLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Backup File">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteOnClick(file.fileName)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DeleteConfirmationBox
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
